import { Fragment, type ReactNode, useState, useEffect } from 'react'

import './styles.scss'

type RadioTabsOption = {
  value: string
  label: ReactNode
  disabled?: boolean
}

interface RadioTabsProps extends React.FieldsetHTMLAttributes<HTMLFieldSetElement> {
  options: RadioTabsOption[]
  defaultValue?: string
  value?: string
  onValueChange?: (optionValue: string) => void
}

export const RadioTabs = ({ options, value, defaultValue, onValueChange, ...props }: RadioTabsProps) => {
  const isControlled = value !== undefined
  const initialValue = isControlled ? value : (defaultValue ?? options[0].value)

  const [selectedValue, setSelectedValue] = useState(initialValue)

  useEffect(() => {
    if (isControlled && value !== selectedValue) {
      setSelectedValue(value)
    }
  }, [isControlled, value, selectedValue])

  const handleChange = (optionValue: string) => {
    if (!isControlled) {
      setSelectedValue(optionValue)
    }
    onValueChange?.(optionValue)
  }

  return (
    <fieldset className="RadioTabs" role="radiogroup" {...props}>
      {options.map(option => (
        <Fragment key={option.value}>
          <input
            type="radio"
            role="radio"
            id={`radio-${option.value}`}
            checked={selectedValue === option.value}
            onChange={() => handleChange(option.value)}
            disabled={option.disabled}
            value={option.value}
          />
          <label className="RadioTabs-tab" htmlFor={`radio-${option.value}`}>
            {option.label}
          </label>
        </Fragment>
      ))}
    </fieldset>
  )
}
